import type {
  CourseListItemDto,
  CoursePhase,
  CoursePreviewThumbnail,
  CourseTypeSummary,
  LessonListItemDto,
  TrainSide,
} from './types';
import { COURSE_PHASE_LABELS, COURSE_PHASES } from './types';
import { formatUtcDateLabel } from '../common/formatUtcDate';
import {
  activeCollectionCourseSlugs,
  BLACK_BROWSE_FAMILIES,
  filterCoursesByBlackCollection,
  filterCoursesByWhiteCollection,
  getBlackBrowseFamily,
  isBlackBrowseFamilySlug,
  isBlackForkCollectionFamily,
  isMultiCourseBlackFamily,
  leafCourseSlugForFamily,
  parseMistakeCoursePartIndex,
  type BlackBrowseFamilyDef,
  type BlackBrowseFamilySlug,
  type BlackForkCollectionSlug,
  type BlackRepertoireForks,
  type WhiteRepertoireCollection,
  type WhiteRepertoireForks,
} from 'endchess-contracts';

/** Classical black repertoire hubs shown above the family grid. */
export const BLACK_REPERTOIRE_HUB_SLUGS = ['e4-e5', 'd4-d5'] as const;

export type BlackRepertoireHubSlug = (typeof BLACK_REPERTOIRE_HUB_SLUGS)[number];

const isBlackRepertoireHubSlug = (
  slug: string,
): slug is BlackRepertoireHubSlug =>
  (BLACK_REPERTOIRE_HUB_SLUGS as readonly string[]).includes(slug);

export const buildCourseTypeSummaries = (
  courses: CourseListItemDto[],
): CourseTypeSummary[] => {
  const counts = new Map<CoursePhase, number>();
  for (const course of courses) {
    counts.set(course.phase, (counts.get(course.phase) ?? 0) + 1);
  }

  return COURSE_PHASES.map((phase) => ({
    phase,
    label: COURSE_PHASE_LABELS[phase],
    courseCount: counts.get(phase) ?? 0,
  }));
};

export const filterCoursesByPhase = (
  courses: CourseListItemDto[],
  phase: CoursePhase,
): CourseListItemDto[] =>
  courses.filter((course) => course.phase === phase);

export const formatPhaseLessonsSubtitle = (
  phaseLabel: string,
  courses: CourseListItemDto[],
  phase?: CoursePhase,
): string => {
  if (phase === 'mistakes') {
    const lessonCount = courses.reduce(
      (total, course) => total + course.lessonCount,
      0,
    );
    if (lessonCount === 0) {
      return 'Mistakes from your imported games appear here.';
    }
    return `${lessonCount} mistake${lessonCount === 1 ? '' : 's'} from your games.`;
  }

  const base = `Browse ${phaseLabel.toLowerCase()} lines from master games.`;
  const confirmDepths = [
    ...new Set(
      courses
        .map((course) => course.confirmDepth)
        .filter((depth) => depth > 0),
    ),
  ].sort((a, b) => a - b);

  if (confirmDepths.length === 0) {
    return base;
  }

  const depthLabel =
    confirmDepths.length === 1
      ? `engine depth ${confirmDepths[0]}`
      : `engine depths ${confirmDepths.join(' and ')}`;

  return `${base} Confirmed at ${depthLabel}.`;
};

const seriesCourseSlugOrder = (slug: string): number => {
  const mistakePartIndex = parseMistakeCoursePartIndex(slug);
  if (mistakePartIndex != null) {
    return mistakePartIndex;
  }
  const match = /^(?:middlegame|endgame)-(\d+)$/.exec(slug);
  return match ? Number.parseInt(match[1], 10) : Number.MAX_SAFE_INTEGER;
};

export const compareOpeningCoursesByLineCount = (
  a: Pick<CourseListItemDto, 'lessonCount' | 'title'>,
  b: Pick<CourseListItemDto, 'lessonCount' | 'title'>,
): number => {
  const lineDiff = b.lessonCount - a.lessonCount;
  return lineDiff !== 0 ? lineDiff : a.title.localeCompare(b.title);
};

/** Keeps numbered engine courses in part order (middlegame-1, middlegame-2, …). */
export const sortPhaseCourses = (
  courses: CourseListItemDto[],
): CourseListItemDto[] =>
  [...courses].sort((a, b) => {
    if (a.phase === 'opening' && b.phase === 'opening') {
      return compareOpeningCoursesByLineCount(a, b);
    }
    const orderDiff = seriesCourseSlugOrder(a.slug) - seriesCourseSlugOrder(b.slug);
    return orderDiff !== 0 ? orderDiff : a.title.localeCompare(b.title);
  });

export const groupCoursesByPhase = (
  courses: CourseListItemDto[],
): Record<CoursePhase, CourseListItemDto[]> => {
  const grouped = Object.fromEntries(
    COURSE_PHASES.map((phase) => [phase, [] as CourseListItemDto[]]),
  ) as Record<CoursePhase, CourseListItemDto[]>;

  for (const course of courses) {
    grouped[course.phase].push(course);
  }

  return grouped;
};

/** Opening → middlegame → endgame, with stable order inside each phase. */
export const sortAllCourses = (
  courses: CourseListItemDto[],
): CourseListItemDto[] => {
  const grouped = groupCoursesByPhase(courses);
  return COURSE_PHASES.flatMap((phase) => sortPhaseCourses(grouped[phase]));
};

export const formatProgressLabel = (
  completedLessonCount: number | undefined,
  lessonCount: number,
): string => {
  const completed = completedLessonCount ?? 0;
  return `${completed}/${lessonCount} lessons`;
};

/** Fork picker options tied to a published course with no lines are shown disabled. */
export const isForkCourseOptionUnavailable = (
  course: Pick<CourseListItemDto, 'lessonCount'> | undefined,
  courseSlug: string | undefined,
): boolean => {
  if (!courseSlug) {
    return false;
  }
  return course == null || course.lessonCount === 0;
};

export const isCourseStarted = (
  course: Pick<
    CourseListItemDto,
    'lastAccessedAt' | 'lastLessonId' | 'completedLessonCount'
  >,
): boolean =>
  Boolean(
    course.lastAccessedAt ||
      course.lastLessonId ||
      (course.completedLessonCount ?? 0) > 0,
  );

export const sortStartedCoursesByRecent = (
  courses: CourseListItemDto[],
): CourseListItemDto[] =>
  courses
    .filter(isCourseStarted)
    .sort((a, b) => {
      const aTime = a.lastAccessedAt
        ? new Date(a.lastAccessedAt).getTime()
        : 0;
      const bTime = b.lastAccessedAt
        ? new Date(b.lastAccessedAt).getTime()
        : 0;
      if (bTime !== aTime) {
        return bTime - aTime;
      }
      return a.title.localeCompare(b.title);
    });

export const formatTrainSideLabel = (trainSide: TrainSide): string =>
  trainSide === 'b' ? 'Black' : 'White';

export const OPENING_TRAIN_SIDES: readonly TrainSide[] = ['w', 'b'] as const;

export const collectCoursePreviewThumbnails = (
  courses: CourseListItemDto[],
  maxCount = 3,
): CoursePreviewThumbnail[] => {
  const thumbnails: CoursePreviewThumbnail[] = [];
  for (const course of courses) {
    for (const thumbnail of course.previewThumbnails ?? []) {
      if (thumbnails.length >= maxCount) {
        return thumbnails;
      }
      thumbnails.push({
        ...thumbnail,
        trainSide: course.trainSide ?? thumbnail.trainSide,
      });
    }
  }
  return thumbnails;
};

export const summarizeCoursesProgress = (
  courses: CourseListItemDto[],
): {
  courseCount: number;
  startedCourseCount: number;
  notStartedCourseCount: number;
  completedLessonCount: number;
  lessonCount: number;
} => {
  let completedLessonCount = 0;
  let lessonCount = 0;
  let startedCourseCount = 0;
  for (const course of courses) {
    completedLessonCount += course.completedLessonCount ?? 0;
    lessonCount += course.lessonCount;
    if (isCourseStarted(course)) {
      startedCourseCount += 1;
    }
  }
  return {
    courseCount: courses.length,
    startedCourseCount,
    notStartedCourseCount: courses.length - startedCourseCount,
    completedLessonCount,
    lessonCount,
  };
};

export const groupOpeningCoursesByTrainSide = (
  courses: CourseListItemDto[],
): Record<TrainSide, CourseListItemDto[]> => {
  const grouped: Record<TrainSide, CourseListItemDto[]> = { w: [], b: [] };
  for (const course of courses) {
    if (course.trainSide === 'b') {
      grouped.b.push(course);
    } else {
      grouped.w.push(course);
    }
  }
  return grouped;
};

export const formatLessonListSecondary = (
  lesson: Pick<LessonListItemDto, 'plies' | 'trainSide' | 'date'>,
  options?: { maxMove?: number },
): string => {
  const maxPlies =
    options?.maxMove != null
      ? Math.max(0, options.maxMove) * 2
      : undefined;
  const shownPlies =
    maxPlies == null ? lesson.plies : Math.min(lesson.plies, maxPlies);
  const parts = [
    `${shownPlies} plies`,
    formatTrainSideLabel(lesson.trainSide),
  ];
  const dateLabel = formatUtcDateLabel(lesson.date);
  if (dateLabel) {
    parts.push(dateLabel);
  }
  return parts.join(' · ');
};

export const isWhiteFamilyCourse = (course: CourseListItemDto): boolean =>
  course.slug.startsWith('white-e4-') || course.slug.startsWith('white-d4-');

export const groupWhiteCoursesByCollection = (
  courses: CourseListItemDto[],
): Record<WhiteRepertoireCollection, CourseListItemDto[]> => {
  const whiteOpening = courses.filter(
    (course) =>
      course.phase === 'opening' &&
      course.trainSide === 'w' &&
      course.repertoireCollection,
  );
  return {
    e4: whiteOpening.filter((course) => course.repertoireCollection === 'e4'),
    d4: whiteOpening.filter((course) => course.repertoireCollection === 'd4'),
  };
};

export const filterWhiteCollectionCourses = (
  courses: CourseListItemDto[],
  collection: WhiteRepertoireCollection,
  forks: WhiteRepertoireForks | undefined,
): CourseListItemDto[] =>
  filterCoursesByWhiteCollection(
    courses.filter(
      (course) =>
        course.phase === 'opening' &&
        course.trainSide === 'w' &&
        course.repertoireCollection === collection,
    ),
    collection,
    forks,
  );

/** Fork-filtered white leaf courses sorted by line count for Opening → White. */
export const buildWhiteFlatBrowseRows = (
  courses: readonly CourseListItemDto[],
  forks: WhiteRepertoireForks | undefined,
): CourseListItemDto[] => {
  const bySlug = new Map<string, CourseListItemDto>();
  for (const collection of ['e4', 'd4'] as const) {
    for (const course of filterWhiteCollectionCourses(
      [...courses],
      collection,
      forks,
    )) {
      bySlug.set(course.slug, course);
    }
  }

  return sortPhaseCourses([...bySlug.values()]);
};

export const summarizeCollectionProgress = (
  courses: CourseListItemDto[],
  collection: WhiteRepertoireCollection,
  forks: WhiteRepertoireForks | undefined,
) =>
  summarizeCoursesProgress(
    filterWhiteCollectionCourses(courses, collection, forks),
  );

export const buildOpeningSideTrainCourses = (
  courses: CourseListItemDto[],
  trainSide: TrainSide,
): CourseListItemDto[] =>
  courses.filter(
    (course) => course.phase === 'opening' && course.trainSide === trainSide,
  );

export const buildPhaseTrainCourses = (
  courses: CourseListItemDto[],
  phase: CoursePhase,
  forks: WhiteRepertoireForks | undefined,
): CourseListItemDto[] => {
  const phaseCourses = filterCoursesByPhase(courses, phase);
  if (phase !== 'opening') {
    return phaseCourses;
  }

  const black = buildOpeningSideTrainCourses(phaseCourses, 'b');
  const e4 = filterWhiteCollectionCourses(phaseCourses, 'e4', forks);
  const d4 = filterWhiteCollectionCourses(phaseCourses, 'd4', forks);
  return [...e4, ...d4, ...black];
};

export const buildCollectionTrainCourses = (
  courses: CourseListItemDto[],
  collection: WhiteRepertoireCollection,
  forks: WhiteRepertoireForks | undefined,
): CourseListItemDto[] =>
  filterWhiteCollectionCourses(courses, collection, forks);

export const whiteCollectionHubTitle = (
  collection: WhiteRepertoireCollection,
): string => (collection === 'e4' ? '1.e4 Repertoire' : '1.d4 Repertoire');

const maxIsoTimestamp = (
  values: readonly (string | undefined)[],
): string | undefined => {
  let best: string | undefined;
  let bestMs = Number.NEGATIVE_INFINITY;
  for (const value of values) {
    if (!value) {
      continue;
    }
    const ms = new Date(value).getTime();
    if (Number.isFinite(ms) && ms > bestMs) {
      bestMs = ms;
      best = value;
    }
  }
  return best;
};

const buildOpeningHubCourse = (
  members: readonly CourseListItemDto[],
  hub: {
    courseId: string;
    slug: string;
    title: string;
    trainSide: TrainSide;
    repertoireCollection?: WhiteRepertoireCollection;
  },
): CourseListItemDto => {
  const progress = summarizeCoursesProgress([...members]);
  const representative = members[0];
  return {
    courseId: hub.courseId,
    slug: hub.slug,
    title: hub.title,
    description: representative?.description ?? '',
    phase: 'opening',
    gamePool: representative?.gamePool ?? 'combined',
    sectionCount: members.reduce((total, course) => total + course.sectionCount, 0),
    lessonCount: progress.lessonCount,
    avgElo: representative?.avgElo ?? 0,
    version: 'hub',
    confirmDepth: representative?.confirmDepth ?? 0,
    trainSide: hub.trainSide,
    repertoireCollection: hub.repertoireCollection,
    completedLessonCount: progress.completedLessonCount,
    completedSectionCount: members.reduce(
      (total, course) => total + (course.completedSectionCount ?? 0),
      0,
    ),
    lastAccessedAt: maxIsoTimestamp(members.map((course) => course.lastAccessedAt)),
    previewThumbnails: collectCoursePreviewThumbnails([...members]),
  };
};

/**
 * Replaces white e4/d4 leafs and multi-course black families with hub cards so
 * recent/dashboard lists match Opening browse (top-level repertoire, not vs Slav).
 */
export const collapseOpeningCoursesToHubs = (
  courses: readonly CourseListItemDto[],
  forks?: WhiteRepertoireForks,
): CourseListItemDto[] => {
  const openingCourses = courses.filter((course) => course.phase === 'opening');
  const nonOpening = courses.filter((course) => course.phase !== 'opening');
  const usedSlugs = new Set<string>();
  const hubs: CourseListItemDto[] = [];

  for (const collection of ['e4', 'd4'] as const) {
    const allMembers = openingCourses.filter(
      (course) =>
        course.trainSide === 'w' && course.repertoireCollection === collection,
    );
    if (allMembers.length === 0) {
      continue;
    }
    for (const course of allMembers) {
      usedSlugs.add(course.slug);
    }
    const activeMembers = filterWhiteCollectionCourses(
      openingCourses,
      collection,
      forks,
    );
    const progressMembers =
      activeMembers.length > 0 ? activeMembers : allMembers;
    const hub = buildOpeningHubCourse(progressMembers, {
      courseId: `hub-white-${collection}`,
      slug: `white-hub-${collection}`,
      title: whiteCollectionHubTitle(collection),
      trainSide: 'w',
      repertoireCollection: collection,
    });
    // Recency should reflect any leaf studied in the collection, including
    // inactive fork alternatives the user may have trained earlier.
    hub.lastAccessedAt = maxIsoTimestamp(
      allMembers.map((course) => course.lastAccessedAt),
    );
    hubs.push(hub);
  }

  for (const row of buildBlackBrowseRows(openingCourses)) {
    if (row.kind === 'orphan') {
      continue;
    }
    for (const course of row.courses) {
      usedSlugs.add(course.slug);
    }
    if (blackFamilyLinksToDrilldown(row.family)) {
      hubs.push(
        buildOpeningHubCourse(row.courses, {
          courseId: `hub-black-${row.family.slug}`,
          slug: `black-hub-${row.family.slug}`,
          title: row.family.title,
          trainSide: 'b',
        }),
      );
    } else {
      const leaf = row.courses[0];
      if (leaf) {
        hubs.push(leaf);
      }
    }
  }

  const remaining = sortPhaseCourses(
    openingCourses.filter((course) => !usedSlugs.has(course.slug)),
  );

  return [...hubs, ...remaining, ...nonOpening];
};

export const activeWhiteCollectionSlugs = (
  collection: WhiteRepertoireCollection,
  forks: WhiteRepertoireForks | undefined,
): Set<string> => new Set(activeCollectionCourseSlugs(collection, forks));

export type BlackBrowseFamilyRow = {
  family: BlackBrowseFamilyDef;
  courses: CourseListItemDto[];
};

export type BlackBrowseOrphanRow = {
  course: CourseListItemDto;
};

export type BlackBrowseRow =
  | ({ kind: 'family' } & BlackBrowseFamilyRow)
  | ({ kind: 'orphan' } & BlackBrowseOrphanRow);

/** Persisted membership is authoritative; catalog lookup supports pre-field rows. */
const blackBrowseFamilySlugForCourse = (
  course: CourseListItemDto,
): BlackBrowseFamilySlug | undefined => {
  if (course.blackBrowseFamilySlug !== undefined) {
    return isBlackBrowseFamilySlug(course.blackBrowseFamilySlug)
      ? course.blackBrowseFamilySlug
      : undefined;
  }
  return BLACK_BROWSE_FAMILIES.find((family) =>
    family.courseSlugs.includes(course.slug),
  )?.slug;
};

/** Published Black opening courses grouped for Opening → Black family list. */
export const buildBlackBrowseRows = (
  courses: readonly CourseListItemDto[],
): BlackBrowseRow[] => {
  const blackOpening = courses.filter(
    (course) => course.phase === 'opening' && course.trainSide === 'b',
  );
  const usedSlugs = new Set<string>();
  const rows: BlackBrowseRow[] = [];

  for (const family of BLACK_BROWSE_FAMILIES) {
    const familyCourses = blackOpening.filter(
      (course) => blackBrowseFamilySlugForCourse(course) === family.slug,
    );
    if (familyCourses.length === 0) {
      continue;
    }
    for (const course of familyCourses) {
      usedSlugs.add(course.slug);
    }
    rows.push({ kind: 'family', family, courses: familyCourses });
  }

  for (const course of sortPhaseCourses(blackOpening)) {
    if (!usedSlugs.has(course.slug)) {
      rows.push({ kind: 'orphan', course });
    }
  }

  return rows;
};

const blackBrowseRowLineCount = (row: BlackBrowseRow): number =>
  row.kind === 'orphan'
    ? row.course.lessonCount
    : row.courses.reduce((total, course) => total + course.lessonCount, 0);

const blackBrowseRowTitle = (row: BlackBrowseRow): string =>
  row.kind === 'orphan' ? row.course.title : row.family.title;

/** Split black browse into repertoire hubs (e4-e5 / d4-d5) vs the rest. */
export const splitBlackBrowseRows = (
  rows: readonly BlackBrowseRow[],
): { hubs: BlackBrowseRow[]; rest: BlackBrowseRow[] } => {
  const hubs: BlackBrowseRow[] = [];
  for (const slug of BLACK_REPERTOIRE_HUB_SLUGS) {
    const hub = rows.find(
      (row) => row.kind === 'family' && row.family.slug === slug,
    );
    if (hub) {
      hubs.push(hub);
    }
  }

  const rest = rows
    .filter(
      (row) =>
        !(row.kind === 'family' && isBlackRepertoireHubSlug(row.family.slug)),
    )
    .sort((a, b) =>
      compareOpeningCoursesByLineCount(
        { lessonCount: blackBrowseRowLineCount(a), title: blackBrowseRowTitle(a) },
        { lessonCount: blackBrowseRowLineCount(b), title: blackBrowseRowTitle(b) },
      ),
    );

  return { hubs, rest };
};

export const filterBlackFamilyCourses = (
  courses: readonly CourseListItemDto[],
  familySlug: BlackBrowseFamilySlug,
): CourseListItemDto[] => {
  const family = getBlackBrowseFamily(familySlug);
  if (!family) {
    return [];
  }
  return sortPhaseCourses(
    courses.filter(
      (course) =>
        course.phase === 'opening' &&
        course.trainSide === 'b' &&
        blackBrowseFamilySlugForCourse(course) === familySlug,
    ),
  );
};

export const filterBlackBrowseFamilyCourses = (
  courses: readonly CourseListItemDto[],
  familySlug: BlackBrowseFamilySlug,
  blackForks?: BlackRepertoireForks,
): CourseListItemDto[] => {
  const familyCourses = filterBlackFamilyCourses(courses, familySlug);
  if (isBlackForkCollectionFamily(familySlug)) {
    return filterCoursesByBlackCollection(
      familyCourses,
      familySlug as BlackForkCollectionSlug,
      blackForks,
    );
  }
  return familyCourses;
};

export const summarizeBlackFamilyProgress = (
  courses: readonly CourseListItemDto[],
  familySlug: BlackBrowseFamilySlug,
) => summarizeCoursesProgress(filterBlackFamilyCourses(courses, familySlug));

export const summarizeBlackBrowseFamilyProgress = (
  courses: readonly CourseListItemDto[],
  familySlug: BlackBrowseFamilySlug,
  blackForks?: BlackRepertoireForks,
) =>
  summarizeCoursesProgress(
    filterBlackBrowseFamilyCourses(courses, familySlug, blackForks),
  );

export const blackFamilyHubTitle = (familySlug: BlackBrowseFamilySlug): string =>
  getBlackBrowseFamily(familySlug)?.title ?? familySlug;

export const blackFamilyLinksToDrilldown = (
  family: BlackBrowseFamilyDef,
): boolean => isMultiCourseBlackFamily(family);

export const blackFamilyLeafCourseSlug = (
  family: BlackBrowseFamilyDef,
): string | undefined => leafCourseSlugForFamily(family);
